import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { ArrowUp, ArrowLeft } from "lucide-react";
import "@/index.css";
import "@/App.css";
import "@/components/ds/ds.css"; // design-system (Button…), chargé au niveau racine
import { AuthProvider, ToastCtxProvider, LangProvider, useAuth } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFlottant from "@/components/WhatsAppFlottant";
import AdminFlottant from "@/components/AdminFlottant";
import EntrepriseFlottant from "@/components/EntrepriseFlottant";
import Accueil from "@/pages/Accueil"; // eager : page d'accueil (premier rendu instantané)

// Toutes les autres pages sont découpées en chunks séparés (React.lazy) : le bundle
// initial reste léger et les grosses pages (Admin, BusinessCenter…) ne se chargent
// qu'à la navigation → application plus légère et plus rapide au démarrage.
const Catalogue = lazy(() => import("@/pages/Catalogue"));
const SolutionProfile = lazy(() => import("@/pages/SolutionProfile"));
const Login = lazy(() => import("@/pages/Login"));
const Admin = lazy(() => import("@/pages/Admin"));
const BusinessCenter = lazy(() => import("@/pages/BusinessCenter"));
const Favoris = lazy(() => import("@/pages/Favoris"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const PaiementProduit = lazy(() => import("@/pages/PaiementProduit"));
const Confidentialite = lazy(() => import("@/pages/Confidentialite"));
const APropos = lazy(() => import("@/pages/APropos"));
const MentionsLegales = lazy(() => import("@/pages/MentionsLegales"));
const CGU = lazy(() => import("@/pages/CGU"));
const CGV = lazy(() => import("@/pages/CGV"));
const EtreReference = lazy(() => import("@/pages/EtreReference"));
const Services = lazy(() => import("@/pages/Services"));
const IdeesSolutions = lazy(() => import("@/pages/IdeesSolutions"));
const ProposerIdee = lazy(() => import("@/pages/ProposerIdee"));
const Articles = lazy(() => import("@/pages/Articles"));
const RevueClient = lazy(() => import("@/pages/RevueClient"));
const Newsletter = lazy(() => import("@/pages/Newsletter"));
const ArticlePage = lazy(() => import("@/pages/ArticlePage"));
const CanevasArticle = lazy(() => import("@/pages/CanevasArticle"));

function ScrollTop() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 300);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  if (!visible) return null;
  return (
    <button className="scroll-top-btn" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} aria-label="Remonter en haut">
      <ArrowUp size={18} />
    </button>
  );
}

// Mémorise la dernière page de navigation (hors fiche solution) pour que le bouton
// « Retour » d'une fiche ramène exactement là où l'utilisateur était.
function MemoRetour() {
  const location = useLocation();
  useEffect(() => {
    const p = location.pathname;
    if (!p.startsWith("/solution/") && !p.startsWith("/a/") && p !== "/connexion") {
      try { sessionStorage.setItem("atlas:retour", p + location.search); } catch { /* stockage indisponible */ }
    }
  }, [location]);
  return null;
}

// Mémoire de défilement : garde la position au rechargement de la page (F5) et
// au retour (bouton précédent), remonte en haut sur une navigation normale.
function ScrollManager() {
  const location = useLocation();
  const navType = useNavigationType();      // "POP" (retour/refresh) | "PUSH" | "REPLACE"
  const premierRendu = useRef(true);
  const key = "atlas:scroll:" + location.pathname + location.search;

  // Le navigateur ne sait pas restaurer une page SPA (contenu chargé après coup) : on gère nous-mêmes.
  useEffect(() => { try { window.history.scrollRestoration = "manual"; } catch { /* non supporté */ } }, []);

  // Sauvegarde continue de la position de la page courante.
  useEffect(() => {
    const save = () => { try { sessionStorage.setItem(key, String(window.scrollY)); } catch { /* stockage indisponible */ } };
    let t; const onScroll = () => { clearTimeout(t); t = setTimeout(save, 120); };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("beforeunload", save);
    return () => { save(); clearTimeout(t); window.removeEventListener("scroll", onScroll); window.removeEventListener("beforeunload", save); };
  }, [key]);

  // Restauration : rechargement (reload) ou retour (POP) → position mémorisée ; sinon haut de page.
  useEffect(() => {
    const navEntry = performance.getEntriesByType("navigation")[0];
    const estReload = premierRendu.current && !!navEntry && navEntry.type === "reload";
    premierRendu.current = false;
    const restaurer = estReload || navType === "POP";
    const y = parseInt(sessionStorage.getItem(key) || "0", 10);
    if (restaurer && y > 0) {
      // Le contenu arrive de façon asynchrone : on réessaie sur quelques frames jusqu'à atteindre la position.
      let tries = 0;
      const go = () => { window.scrollTo(0, y); if (Math.abs(window.scrollY - y) > 4 && tries++ < 40) requestAnimationFrame(go); };
      requestAnimationFrame(go);
    } else if (navType !== "POP") {
      window.scrollTo(0, 0);
    }
    // eslint-disable-next-line
  }, [location.pathname, location.search]);
  return null;
}

// Bouton « Retour » flottant et DYNAMIQUE : revient à la page précédente réelle
// (historique du navigateur) ; en accès direct, retombe sur la dernière page mémorisée ou l'accueil.
function BoutonRetour() {
  const location = useLocation();
  const nav = useNavigate();
  const p = location.pathname;
  const masque = p === "/" || p === "/solutions" || p === "/connexion" || p.startsWith("/admin") || p.startsWith("/espace-entreprise") || p.startsWith("/revue/");
  if (masque) return null;
  const retour = () => {
    const idx = (window.history.state && window.history.state.idx) || 0;
    if (idx > 0) { nav(-1); return; }
    const memo = sessionStorage.getItem("atlas:retour");
    nav(memo && memo !== p + location.search ? memo : "/");
  };
  return (
    <button className="retour-flottant" onClick={retour} aria-label="Retour" data-testid="bouton-retour">
      <ArrowLeft size={22} />
    </button>
  );
}

function Protected({ role, children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="loading">Chargement…</div>;
  if (!user) return <Navigate to="/connexion" replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
}

function Shell() {
  return (
    <>
      <a href="#main-content" className="skip-link">Aller au contenu</a>
      <MemoRetour />
      <ScrollManager />
      <Navbar />
      <BoutonRetour />
      <main id="main-content" tabIndex={-1}>
      <Suspense fallback={<div className="loading">Chargement…</div>}>
      <Routes>
        <Route path="/" element={<Accueil />} />
        <Route path="/solutions" element={<Catalogue />} />
        <Route path="/solution/:id" element={<SolutionProfile />} />
        <Route path="/favoris" element={<Favoris />} />
        <Route path="/abonnements" element={<Pricing />} />
        <Route path="/paiement" element={<PaiementProduit />} />
        <Route path="/connexion" element={<Login />} />
        <Route path="/admin" element={<Protected role="admin"><Admin /></Protected>} />
        <Route path="/espace-entreprise" element={<Protected><BusinessCenter /></Protected>} />
        <Route path="/business" element={<Navigate to="/espace-entreprise" replace />} />
        <Route path="/confidentialite" element={<Confidentialite />} />
        <Route path="/a-propos" element={<APropos />} />
        <Route path="/mentions-legales" element={<MentionsLegales />} />
        <Route path="/cgu" element={<CGU />} />
        <Route path="/cgv" element={<CGV />} />
        <Route path="/services" element={<Services />} />
        <Route path="/etre-reference" element={<EtreReference />} />
        <Route path="/idees" element={<IdeesSolutions />} />
        <Route path="/proposer-idee" element={<ProposerIdee />} />
        <Route path="/articles" element={<Articles />} />
        <Route path="/article/:slug" element={<ArticlePage />} />
        <Route path="/revue/:token" element={<RevueClient />} />
        <Route path="/newsletter" element={<Newsletter />} />
        <Route path="/admin/editorial/nouveau" element={<Protected role="admin"><CanevasArticle /></Protected>} />
        <Route path="/admin/editorial/:id" element={<Protected role="admin"><CanevasArticle /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      </main>
      <Footer />
      <WhatsAppFlottant />
      <AdminFlottant />
      <EntrepriseFlottant />
      <ScrollTop />
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ToastCtxProvider>
        <LangProvider>
          <BrowserRouter>
            <Shell />
          </BrowserRouter>
        </LangProvider>
      </ToastCtxProvider>
    </AuthProvider>
  );
}
